"use client";

import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check } from "lucide-react";
import { CUSTOM_ICONS } from "@/lib/icons";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import { cn } from "@/lib/utils";
import { trackUetConversion } from "@/lib/analytics/uet";
import { COUNTRIES } from "@/components/ui/PhoneInput";
import { CountrySelectorList } from "@/components/ui/CountryCodePicker";
import { CountryFlag } from "@/components/ui/CountryFlag";

interface CtaSectionProps {
  title?: string;
  description?: string;
  image?: any;
  formConfig?: any;
  locale?: string;
}

/**
 * Premium Custom Dropdown Component
 * Enhanced with data-lenis-prevent and event propagation fixes for smooth scrolling compatibility.
 */
function CustomDropdown({
  label,
  options,
  placeholder,
  value,
  onChange,
  className,
}: any) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fix Lenis scroll conflict
  useEffect(() => {
    const el = listRef.current;
    if (!el || !isOpen) return;

    const stopPropagation = (e: any) => e.stopPropagation();

    el.addEventListener("wheel", stopPropagation, { passive: false });
    el.addEventListener("touchmove", stopPropagation, { passive: false });

    return () => {
      el.removeEventListener("wheel", stopPropagation);
      el.removeEventListener("touchmove", stopPropagation);
    };
  }, [isOpen]);

  const selectedOption = options?.find((opt: any) => opt.value === value);

  return (
    <div className="space-y-2 relative" ref={dropdownRef}>
      {label && (
        <label className="text-white text-[16px] font-semibold">{label}</label>
      )}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full h-11 bg-black/30 border rounded-[10px] px-4 flex items-center justify-between cursor-pointer transition-all duration-300 hover:bg-black/50",
          isOpen
            ? "border-[#FFF28E]/60 shadow-[0_0_15px_rgba(255,242,142,0.2)]"
            : "border-white/20",
          className
        )}
      >
        <span
          className={cn(
            "text-[16px]",
            selectedOption ? "text-white" : "text-white/40",
          )}
        >
          {selectedOption ? selectedOption.label : placeholder?.trim()}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <ChevronDown className="w-5 h-5 text-white/60" />
        </motion.div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full start-0 w-full mt-2 bg-[#2D2D1F] border border-white/10 rounded-[15px] shadow-2xl z-[500] overflow-hidden backdrop-blur-xl"
          >
            {/* data-lenis-prevent added to allow internal scrolling */}
            <div
              ref={listRef}
              data-lenis-prevent
              className="max-h-[350px] overflow-y-auto py-2 custom-scrollbar"
            >
              {options?.map((opt: any) => (
                <div
                  key={opt.id}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`px-6 py-4 flex items-center justify-between cursor-pointer transition-colors hover:bg-white/5 ${value === opt.value ? "bg-white/10 text-[#FFF28E]" : "text-white/80"}`}
                >
                  <span className="text-[16px] font-medium">{opt.label}</span>
                  {value === opt.value && (
                    <Check className="w-5 h-5 text-[#FFF28E]" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * CtaSection (Contact Form)
 * Refined with Double-Layer Ghosting Effect and Lenis-compatible Dropdown.
 */
export function CtaSection({
  title,
  description,
  image,
  formConfig,
  locale = "en",
}: CtaSectionProps) {
  const [formState, setFormState] = useState<any>({
    name: "",
    company: "",
    email: "",
    whatsapp: "",
    "project-type": "",
    "primary-requirement": "",
    "other-primary-requirement": "",
    "specific-requirements-project-description": "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mergedConfig = useMemo(() => {
    return typeof formConfig === "string"
      ? { id: formConfig }
      : formConfig || {};
  }, [formConfig]);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isGloballyAccepted, setIsGloballyAccepted] = useState(false);
  const STORAGE_KEY = "busrom_privacy_consent";

  const showPrivacy = !!mergedConfig?.privacyConsentText;
  const SECTION_HEIGHT = 1080;
  const IMAGE_HEIGHT = SECTION_HEIGHT;
  const IMAGE_WIDTH = 689;
  const BG_TOP_OFFSET_SCALED = 120;

  // Check global consent status on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const consent = localStorage.getItem(STORAGE_KEY);
      if (consent === "true") {
        setIsGloballyAccepted(true);
        setPrivacyAccepted(true);
      }
    }
  }, []);

  // Sync with global storage when accepted in this form
  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, "");
    setFormState((prev: any) => ({
      ...prev,
      whatsapp: digits ? `+${selectedCountry[2]}${digits}` : "",
    }));
  };

  const handlePrivacyToggle = (checked: boolean) => {
    setPrivacyAccepted(checked);
    if (checked && typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, "true");
      setIsGloballyAccepted(true);
      window.dispatchEvent(new Event("storage"));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setUploadedFile(file);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isSubmitting) return;
    if (!!mergedConfig?.privacyConsentText && !privacyAccepted) return;

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      let fileUrl = "";
      if (uploadedFile) {
        const formData = new FormData();
        formData.append("file", uploadedFile);
        const uploadRes = await fetch("/api/media-upload", {
          method: "POST",
          body: formData,
        });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          fileUrl = uploadData.url;
        }
      }

      const submissionData = {
        formId: mergedConfig?.id,
        formName:
          mergedConfig?.name ||
          mergedConfig?.displayName ||
          "One-Stop Solution Contact",
        data: {
          ...formState,
          ...(fileUrl ? { attachment: fileUrl } : {}),
        },
        locale,
        sourcePage: typeof window !== "undefined" ? window.location.href : "",
        userLocalTime: new Date().toString(),
      };

      const response = await fetch("/api/form-submissions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) throw new Error("Submission failed");

      // GTM Tracking
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'form_submit_success',
          form_id: mergedConfig?.name || mergedConfig?.displayName || "one-stop-solution-contact",
          form_name: mergedConfig?.name || mergedConfig?.displayName || "One-Stop Solution Contact"
        });
      }
      trackUetConversion('Submit', 'Request_Quote', 5, 'Lead');

      setSubmitStatus("success");
      setFormState({
        name: "",
        company: "",
        email: "",
        whatsapp: "",
        "project-type": "",
        "primary-requirement": "",
        "other-primary-requirement": "",
        "specific-requirements-project-description": "",
      });
      setUploadedFile(null);
      setTimeout(() => setSubmitStatus("idle"), 5000);
    } catch (error) {
      console.error("[CtaSection] Submission Error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const consent = localStorage.getItem(STORAGE_KEY);
      if (consent === "true") {
        setIsGloballyAccepted(true);
        setPrivacyAccepted(true);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const [isMobile, setIsMobile] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    COUNTRIES.find((c) => c[1] === "US") || COUNTRIES[0],
  );
  const [openCountrySelector, setOpenCountrySelector] = useState(false);
  const [openCountrySelectorMobile, setOpenCountrySelectorMobile] = useState(false);
  const countrySelectorRef = useRef<HTMLDivElement>(null);
  const countrySelectorRefMobile = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1025);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);

    const handleClickOutside = (event: MouseEvent) => {
      if (
        countrySelectorRef.current &&
        !countrySelectorRef.current.contains(event.target as Node)
      ) {
        setOpenCountrySelector(false);
      }
      if (
        countrySelectorRefMobile.current &&
        !countrySelectorRefMobile.current.contains(event.target as Node)
      ) {
        setOpenCountrySelectorMobile(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("resize", checkMobile);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fields = mergedConfig?.fields || [];
  const getField = (name: string) =>
    fields.find((f: any) => f.fieldName === name);


  /**
   * Safe Translation Helper
   * If the value is a string, returns it.
   * If it's an object { en, cn, ja... }, picks the correct locale.
   */
  const getTranslation = (val: any) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") {
      return val[locale] || val["en"] || Object.values(val)[0] || "";
    }
    return "";
  };

  return (
    <section
      id="contact-form"
      className="relative w-full overflow-hidden flex flex-col items-center bg-transparent z-10 transition-[height] duration-500 ease-in-out justify-start pb-24 xl:pb-0"
      style={{ height: "auto" }}
    >
      <style jsx>{`
        @media (min-width: 1280px) {
          #contact-form {
            height: ${SECTION_HEIGHT}px !important;
          }
        }
        .cta-input-el {
          color: #FFFFFF !important;
          opacity: 1 !important;
          -webkit-text-fill-color: #FFFFFF !important;
          caret-color: white !important;
          font-family: var(--font-anaheim), sans-serif !important;
          font-weight: 600 !important;
        }
        .cta-input-el::placeholder {
          color: rgba(255, 255, 255, 0.4) !important;
          opacity: 1 !important;
          -webkit-text-fill-color: rgba(255, 255, 255, 0.4) !important;
          font-family: var(--font-anaheim), sans-serif !important;
          font-weight: 600 !important;
        }
        .cta-input-el:-webkit-autofill,
        .cta-input-el:-webkit-autofill:hover,
        .cta-input-el:-webkit-autofill:focus,
        .cta-input-el:-webkit-autofill:active {
          -webkit-text-fill-color: #FFFFFF !important;
          background-color: transparent !important;
          -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
          transition: background-color 5000000s ease-in-out 0s !important;
          caret-color: white !important;
          font-family: var(--font-anaheim), sans-serif !important;
          font-weight: 600 !important;
          outline: none !important;
        }
        .cta-input-el:hover {
          /* No custom hover styles */
        }
        .cta-input-el:focus,
        .cta-input-el:active,
        .cta-input-el:focus-visible {
          outline: none !important;
          box-shadow: none !important;
          /* No custom focus styles */
        }
      `}</style>
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>

      {/* 1. Green Base Background */}
      <div
        className="absolute inset-x-0 bottom-0 bg-[#5E571F] z-0 hidden xl:block"
        style={{ top: `${BG_TOP_OFFSET_SCALED}px` }}
      />
      <div className="absolute inset-0 bg-[#5E571F] z-0 xl:hidden" />

      {/* 2. Visual Layer: Double-Layer Images (Solid Foreground + Transparent Ghost) - Desktop Only */}
      <div
        className="absolute end-0 bottom-0 z-10 pointer-events-none hidden xl:block"
        style={{
          width: `${IMAGE_WIDTH}px`,
          height: `${IMAGE_HEIGHT}px`,
        }}
      >
        {image && (
          <div className="relative w-full h-full overflow-visible">
            <motion.div
              initial={{ opacity: 0, x: 0, y: 0 }}
              whileInView={{ opacity: 0.25, x: -80, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              className="absolute inset-0"
            >
              <OptimizedImage
                image={image}
                alt="Ghost Layer"
                className="w-full h-full object-cover"
                size="large"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0, y: 30 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2 }}
              className="absolute inset-0 z-10"
            >
              <OptimizedImage
                image={image}
                alt="Solid Layer"
                className="w-full h-full object-cover"
                size="large"
              />
            </motion.div>
          </div>
        )}
      </div>

      {/* 3. Mobile Content Container (Responsive Column) */}
      <div className="relative z-20 w-full xl:hidden flex flex-col items-center px-6 py-12 gap-10">
        <div className="w-full max-w-[500px] text-center space-y-4">
          <AnimatePresence>
            {submitStatus !== "idle" && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={cn(
                  "mb-4 px-4 py-2 rounded-lg text-center font-bold",
                  submitStatus === "success"
                    ? "bg-[#FFF28E]/10 text-[#FFF28E]"
                    : "bg-red-500/10 text-red-400",
                )}
                style={{ fontFamily: "var(--font-anaheim)" }}
              >
                {submitStatus === "success"
                  ? getTranslation(mergedConfig?.successMessage)
                  : getTranslation(mergedConfig?.errorNetworkMessage)}
              </motion.div>
            )}
          </AnimatePresence>
          <h2
            className="text-[32px] md:text-[40px] font-extrabold text-[#FFF28E] leading-tight tracking-tighter"
            style={{ fontFamily: "var(--font-anaheim)" }}
          >
            {getTranslation(title || mergedConfig?.displayName)}
          </h2>
          <p
            className="text-[16px] md:text-[18px] font-semibold text-white/90 leading-relaxed"
            style={{ fontFamily: "var(--font-anaheim)" }}
          >
            {getTranslation(description || mergedConfig?.description)}
          </p>
        </div>

        <form
          id={mergedConfig?.name || mergedConfig?.displayName || "one-stop-solution-contact"}
          onSubmit={handleSubmit}
          className="w-full max-w-[540px] flex flex-col gap-4"
        >
          <input
            type="text"
            value={formState["name"]}
            onChange={(e: any) =>
              setFormState({ ...formState, name: e.target.value })
            }
            spellCheck={false}
            placeholder={getTranslation(getField("name")?.placeholder)?.trim()}
            name="name"
            autoComplete="name"
            className="cta-input-el w-full h-[68px] bg-white/5 border border-white/20 rounded-[15px] px-5 text-[16px]"
            required
          />
          <input
            type="text"
            value={formState["company"]}
            onChange={(e) =>
              setFormState({ ...formState, company: e.target.value })
            }
            spellCheck={false}
            placeholder={getTranslation(
              getField("company")?.placeholder,
            )?.trim()}
            name="organization"
            autoComplete="organization"
            className="cta-input-el w-full h-[68px] bg-white/5 border border-white/20 rounded-[15px] px-5 text-[16px]"
          />
          <input
            type="email"
            value={formState["email"]}
            onChange={(e) =>
              setFormState({ ...formState, email: e.target.value })
            }
            spellCheck={false}
            placeholder={getTranslation(getField("email")?.placeholder)?.trim()}
            name="email"
            autoComplete="email"
            className="cta-input-el w-full h-[68px] bg-white/5 border border-white/20 rounded-[15px] px-5 text-[16px]"
            required
          />
          <div
            className={cn(
                "flex items-stretch bg-white/5 border border-white/20 rounded-[15px] relative",
                openCountrySelectorMobile ? "z-[600]" : "z-0"
              )}
            ref={countrySelectorRefMobile}
            style={{ 
              width: "100%", 
              height: "68px"
            }}
          >
            <button
              type="button"
              onClick={() => setOpenCountrySelectorMobile(!openCountrySelectorMobile)}
              disabled={isSubmitting}
              className="flex items-center gap-1 ps-2 pe-1.5 border-e border-white/20 flex-shrink-0"
            >
              <div className="w-6 h-4 md:w-8 md:h-5 flex-shrink-0">
                <CountryFlag
                  countryCode={selectedCountry[1]}
                  className="w-full h-full rounded-[2px] object-cover"
                />
              </div>
              <span className="text-white font-anaheim font-semibold text-sm">
                +{selectedCountry[2]}
              </span>
              <svg
                className={cn(
                  "w-3 h-3 text-white/60 transition-transform duration-300 flex-shrink-0",
                  openCountrySelectorMobile && "rotate-180"
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            <input
              type="tel"
              value={
                formState["whatsapp"]?.replace(
                  `+${selectedCountry[2]}`,
                  "",
                ) || ""
              }
              onChange={(e) =>
                handlePhoneChange(e.target.value)
              }
              placeholder={getTranslation(
                getField("whatsapp")?.placeholder,
              )?.trim()}
              disabled={isSubmitting}
              name="tel"
              autoComplete="tel"
              className="cta-input-el flex-1 bg-transparent ps-2 pe-3 border-none outline-none"
            />

            {openCountrySelectorMobile && (
              <div className="absolute start-0 top-full mt-2 z-[100]">
                <CountrySelectorList
                  onSelect={(country) => {
                    setSelectedCountry(country);
                    setOpenCountrySelectorMobile(false);
                  }}
                  onClose={() => setOpenCountrySelectorMobile(false)}
                  className="shadow-2xl"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <CustomDropdown
              placeholder={getTranslation(
                getField("project-type")?.placeholder,
              )}
              options={getField("project-type")?.options}
              value={formState["project-type"]}
              onChange={(val: string) =>
                setFormState({ ...formState, "project-type": val })
              }
              className="!h-[68px] !bg-white/5 !border !border-white/20 !rounded-[15px] !text-[16px] !px-5"
            />
            <CustomDropdown
              placeholder={getTranslation(
                getField("primary-requirement")?.placeholder,
              )}
              options={getField("primary-requirement")?.options}
              value={formState["primary-requirement"]}
              onChange={(val: string) =>
                setFormState({ ...formState, "primary-requirement": val })
              }
              className="!h-[68px] !bg-white/5 !border !border-white/20 !rounded-[15px] !text-[16px] !px-5"
            />
          </div>

          <input
            type="text"
            value={formState["other-primary-requirement"]}
            onChange={(e) =>
              setFormState({
                ...formState,
                "other-primary-requirement": e.target.value,
              })
            }
            spellCheck={false}
            placeholder={getTranslation(
              getField("other-primary-requirement")?.placeholder,
            )}
            className="cta-input-el w-full h-[68px] bg-white/5 border border-white/20 rounded-[15px] px-5 text-[16px]"
          />

          <textarea
            value={formState["specific-requirements-project-description"]}
            onChange={(e) =>
              setFormState({
                ...formState,
                "specific-requirements-project-description": e.target.value,
              })
            }
            spellCheck={false}
            placeholder={getTranslation(
              getField("specific-requirements-project-description")
                ?.placeholder,
            )}
            className="cta-input-el w-full h-36 bg-white/5 border border-white/20 rounded-[15px] p-5 text-[16px] resize-none overflow-y-auto"
          />

          <div className="flex flex-col gap-6 mt-4 items-center">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-3 text-white transition-all w-fit border border-white/20 rounded-full px-6 py-2.5 hover:bg-white hover:text-[#5E571F] hover:border-white"
            >
              <svg
                width="20"
                height="20"
                viewBox={CUSTOM_ICONS.upload.viewBox}
                fill="none"
                className="flex-shrink-0"
              >
                <path d={CUSTOM_ICONS.upload.path} fill="currentColor" />
              </svg>
              <span
                className="text-[16px] font-bold tracking-widest"
                style={{ fontFamily: "var(--font-anaheim)" }}
              >
                {uploadedFile
                  ? uploadedFile.name
                  : getTranslation(getField("file")?.label)}
              </span>
            </button>

            {mergedConfig?.privacyConsentText && (
              <div
                className="flex items-start gap-3 cursor-pointer w-full max-w-[320px]"
                onClick={() => handlePrivacyToggle(!privacyAccepted)}
              >
                <div
                  className={cn(
                    "mt-1 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-all",
                    privacyAccepted
                      ? "bg-[#B2A224] border-[#B2A224]"
                      : "border-white/30 bg-black/30",
                  )}
                >
                  {privacyAccepted && <Check className="w-3 h-3 text-white" />}
                </div>
                <p className={cn(
                  "text-[12px] md:text-[14px] leading-relaxed text-start select-none transition-opacity duration-300",
                  privacyAccepted ? "text-white opacity-100" : "text-white opacity-70"
                )}>
                  {getTranslation(mergedConfig.privacyConsentText)}
                </p>
              </div>
            )}

            <motion.button
              type="submit"
              initial={{ rotate: 0, scale: 1 }}
              animate={{ rotate: [0, -3, 3, -3, 3, 0] }}
              whileHover={{
                rotate: 0,
                scale: 1.05,
                transition: { scale: { duration: 0.3, ease: "easeOut" } },
              }}
              transition={{
                rotate: {
                  duration: 0.5,
                  repeat: Infinity,
                  repeatDelay: 2,
                  ease: "linear",
                },
              }}
              disabled={
                isSubmitting ||
                (!!mergedConfig?.privacyConsentText && !privacyAccepted)
              }
              className={cn(
                "w-full max-w-[320px] bg-[#B2A224] font-anaheim text-white text-[24px] font-black rounded-full shadow-lg transition-colors duration-300",
                "h-14 flex items-center justify-center",
                isSubmitting ||
                  (!!mergedConfig?.privacyConsentText && !privacyAccepted)
                  ? "grayscale opacity-80 cursor-not-allowed"
                  : "hover:bg-white hover:text-[#B2A224]",
              )}
              style={{
                transformOrigin: "center",
              }}
            >
              {isSubmitting
                ? getTranslation(mergedConfig?.submittingText)
                : getTranslation(mergedConfig?.submitButtonText)}
            </motion.button>
          </div>
        </form>
      </div>

      {/* 4. Desktop Content Container (Form Area) - No Scale */}
      <div
        className="relative z-20 flex-shrink-0 hidden xl:flex items-start overflow-visible transition-[height] duration-500 ease-in-out"
        style={{
          width: `1344px`,
          height: `${SECTION_HEIGHT}px`,
        }}
      >
        <div className="ps-[107px] w-[770px] pt-[200px]">
          <div className="mb-4">
            <AnimatePresence>
              {submitStatus !== "idle" && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className={cn(
                    "mb-2 text-[16px] font-bold tracking-widest",
                    submitStatus === "success"
                      ? "text-[#FFF28E]"
                      : "text-red-400",
                  )}
                  style={{ fontFamily: "var(--font-anaheim)" }}
                >
                  {submitStatus === "success"
                    ? getTranslation(mergedConfig?.successMessage)
                    : getTranslation(mergedConfig?.errorNetworkMessage)}
                </motion.div>
              )}
            </AnimatePresence>
            <h2
              className="text-[42px] font-extrabold text-[#FFF28E] leading-[1.1] mb-4 tracking-tighter"
              style={{ fontFamily: "var(--font-anaheim)" }}
            >
              {getTranslation(title || mergedConfig?.displayName)}
            </h2>
            <p
              className="text-[16px] font-semibold text-white/90 leading-[29px] max-w-[576px]"
              style={{ fontFamily: "var(--font-anaheim)" }}
            >
              {getTranslation(description || mergedConfig?.description)}
            </p>
          </div>

          <form
            id={mergedConfig?.name || mergedConfig?.displayName || "one-stop-solution-contact"}
            onSubmit={handleSubmit}
            className="grid grid-cols-2 gap-x-8 gap-y-3 w-[541px] relative"
          >
            <input
              type="text"
              value={formState["name"]}
              onChange={(e) =>
                setFormState({ ...formState, name: e.target.value })
              }
              spellCheck={false}
              placeholder={getTranslation(
                getField("name")?.placeholder,
              )?.trim()}
              name="name"
              autoComplete="name"
              className="cta-input-el h-[44px] bg-black/30 border border-white/20 rounded-[10px] px-4 text-[16px]"
              required
            />
            <input
              type="text"
              value={formState["company"]}
              onChange={(e) =>
                setFormState({ ...formState, company: e.target.value })
              }
              spellCheck={false}
              placeholder={getTranslation(
                getField("company")?.placeholder,
              )?.trim()}
              name="organization"
              autoComplete="organization"
              className="cta-input-el h-[44px] bg-black/30 border border-white/20 rounded-[10px] px-4 text-[16px]"
            />

            <input
              type="email"
              value={formState["email"]}
              onChange={(e) =>
                setFormState({ ...formState, email: e.target.value })
              }
              spellCheck={false}
              placeholder={getTranslation(
                getField("email")?.placeholder,
              )?.trim()}
              name="email"
              autoComplete="email"
              className="cta-input-el h-[44px] bg-black/30 border border-white/20 rounded-[10px] px-4 text-[16px]"
              required
            />
            <div
              className={cn(
                "flex items-stretch bg-black/30 border border-white/20 rounded-[10px] relative",
                openCountrySelector ? "z-[600]" : "z-0"
              )}
              ref={countrySelectorRef}
              style={{ 
                width: "100%", 
                height: "44px"
              }}
            >
              <button
                type="button"
                onClick={() => setOpenCountrySelector(!openCountrySelector)}
                disabled={isSubmitting}
                className="flex items-center gap-1 ps-2 pe-1.5 border-e border-white/20 flex-shrink-0"
              >
                <div className="w-5 h-3 md:w-6 md:h-4 flex-shrink-0">
                  <CountryFlag
                    countryCode={selectedCountry[1]}
                    className="w-full h-full rounded-[1px] object-cover"
                  />
                </div>
                <span className="text-white font-anaheim font-semibold text-sm">
                  +{selectedCountry[2]}
                </span>
                <svg
                  className={cn(
                    "w-3 h-3 text-white/60 transition-transform duration-300 flex-shrink-0",
                    openCountrySelector && "rotate-180"
                  )}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <input
                type="tel"
                value={
                  formState["whatsapp"]?.replace(
                    `+${selectedCountry[2]}`,
                    "",
                  ) || ""
                }
                onChange={(e) =>
                  handlePhoneChange(e.target.value)
                }
                placeholder={getTranslation(
                  getField("whatsapp")?.placeholder,
                )?.trim()}
                disabled={isSubmitting}
                name="tel"
                autoComplete="tel"
                className="cta-input-el flex-1 bg-transparent ps-2 pe-3 border-none outline-none"
              />

              {openCountrySelector && (
                <div className="absolute start-0 top-full mt-2 z-[100]">
                  <CountrySelectorList
                    onSelect={(country) => {
                      setSelectedCountry(country);
                      setOpenCountrySelector(false);
                    }}
                    onClose={() => setOpenCountrySelector(false)}
                    className="shadow-2xl"
                  />
                </div>
              )}
            </div>

            <div className="relative z-[502]">
              <CustomDropdown
                label={getTranslation(getField("project-type")?.label)}
                placeholder={getTranslation(
                  getField("project-type")?.placeholder,
                )?.trim()}
                options={getField("project-type")?.options}
                value={formState["project-type"]}
                onChange={(val: string) =>
                  setFormState({ ...formState, "project-type": val })
                }
              />
            </div>
            <div className="relative z-[501]">
              <CustomDropdown
                label={getTranslation(getField("primary-requirement")?.label)}
                placeholder={getTranslation(
                  getField("primary-requirement")?.placeholder,
                )?.trim()}
                options={getField("primary-requirement")?.options}
                value={formState["primary-requirement"]}
                onChange={(val: string) =>
                  setFormState({ ...formState, "primary-requirement": val })
                }
              />
            </div>

            <div />
            <input
              type="text"
              value={formState["other-primary-requirement"]}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  "other-primary-requirement": e.target.value,
                })
              }
              spellCheck={false}
              placeholder={getTranslation(
                getField("other-primary-requirement")?.placeholder,
              )?.trim()}
              className="cta-input-el w-full h-[44px] bg-black/30 border border-white/20 rounded-[10px] px-4 text-[16px]"
            />

            <div className="col-span-2">
              <textarea
                value={formState["specific-requirements-project-description"]}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    "specific-requirements-project-description": e.target.value,
                  })
                }
                spellCheck={false}
                placeholder={getTranslation(
                  getField("specific-requirements-project-description")
                    ?.placeholder,
                )?.trim()}
                className="cta-input-el w-full h-[105px] bg-black/30 border border-white/20 rounded-[10px] p-4 text-[16px] resize-none overflow-y-auto"
              />
            </div>

            <div className="flex flex-col gap-3 w-[541px]">
              <div className="flex flex-col gap-3 w-full">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-3 text-white transition-all w-fit border border-white/20 rounded-full px-6 py-2.5 hover:bg-white hover:text-[#5E571F] hover:border-white"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox={CUSTOM_ICONS.upload.viewBox}
                    fill="none"
                    className="flex-shrink-0"
                  >
                    <path d={CUSTOM_ICONS.upload.path} fill="currentColor" />
                  </svg>
                  <span
                    className="text-[16px] font-bold tracking-widest"
                    style={{ fontFamily: "var(--font-anaheim)" }}
                  >
                    {uploadedFile
                      ? uploadedFile.name
                      : getTranslation(getField("file")?.label)}
                  </span>
                </button>

                {/* Privacy Consent Checkbox - Always show if text is present */}
                {mergedConfig?.privacyConsentText && (
                  <div
                    className="flex items-start gap-3 cursor-pointer group"
                    onClick={() => handlePrivacyToggle(!privacyAccepted)}
                  >
                    <div
                      className={cn(
                        "mt-1 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-all",
                        privacyAccepted
                          ? "bg-[#B2A224] border-[#B2A224]"
                          : "border-white/30 bg-black/30",
                      )}
                    >
                      {privacyAccepted && (
                        <svg
                          className="w-3.5 h-3.5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={4}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                    <p className={cn(
                      "text-[14px] leading-relaxed max-w-[700px] whitespace-pre-line select-none text-start transition-colors",
                      privacyAccepted ? "text-white" : "text-white/70"
                    )}>
                      {getTranslation(mergedConfig.privacyConsentText)}
                    </p>
                  </div>
                )}

                <motion.button
                  type="submit"
                  style={{ transformOrigin: "center" }}
                  initial={{ rotate: 0, scale: 1 }}
                  animate={{ rotate: [0, -3, 3, -3, 3, 0] }}
                  whileHover={{
                    rotate: 0,
                    scale: 1.05,
                    transition: { scale: { duration: 0.3, ease: "easeOut" } },
                  }}
                  transition={{
                    rotate: {
                      duration: 0.5,
                      repeat: Infinity,
                      repeatDelay: 2,
                      ease: "linear",
                    },
                  }}
                  disabled={
                    isSubmitting ||
                    (!!mergedConfig?.privacyConsentText && !privacyAccepted)
                  }
                  className={cn(
                    "w-[419px] bg-[#B2A224] text-white text-[40px] font-anaheim font-black rounded-[63px] shadow-2xl transition-colors duration-300 self-center",
                    "min-h-[83px] h-auto py-4 whitespace-pre-line leading-tight px-8",
                    isSubmitting ||
                      (!!mergedConfig?.privacyConsentText && !privacyAccepted)
                      ? "grayscale opacity-80 cursor-not-allowed"
                      : "hover:bg-white hover:text-[#B2A224]",
                  )}
                >
                  {isSubmitting
                    ? getTranslation(mergedConfig?.submittingText)
                    : getTranslation(mergedConfig?.submitButtonText)}
                </motion.button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
